import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../../components/ui/Card';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

// TypeScript interfaces
interface ReconciliationRecord {
  id: string;
  batch_id: string;
  import_type: 'uber_income' | 'salik_tolls' | 'traffic_fines';
  import_date: string;
  file_name: string | null;
  total_amount: number;
  record_count: number;
  calculated_total: number | null;
  difference: number | null;
  status: 'matched' | 'discrepancy' | 'pending';
  notes: string | null;
  created_at: string;
}

interface ReconciliationStats {
  totalReconciliations: number;
  matched: number;
  discrepancies: number;
  pending: number;
  totalAmount: number;
  averageAccuracy: number;
}

type ImportTypeFilter = 'all' | 'uber_income' | 'salik_tolls' | 'traffic_fines';
type StatusFilter = 'all' | 'matched' | 'discrepancy' | 'pending';

export const ReconciliationDashboard: React.FC<{ navigation: any }> = ({ navigation }) => {
  const [reconciliations, setReconciliations] = useState<ReconciliationRecord[]>([]);
  const [stats, setStats] = useState<ReconciliationStats>({
    totalReconciliations: 0,
    matched: 0,
    discrepancies: 0,
    pending: 0,
    totalAmount: 0,
    averageAccuracy: 100,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [importTypeFilter, setImportTypeFilter] = useState<ImportTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    loadReconciliations();
  }, [importTypeFilter, statusFilter]);

  const loadReconciliations = async () => {
    try {
      setLoading(true);

      // Build query with filters
      let query = supabase
        .from('batch_reconciliation')
        .select('*')
        .order('import_date', { ascending: false });

      if (importTypeFilter !== 'all') {
        query = query.eq('import_type', importTypeFilter);
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const records = data || [];
      setReconciliations(records);

      // Calculate statistics
      const totalReconciliations = records.length;
      const matched = records.filter(r => r.status === 'matched').length;
      const discrepancies = records.filter(r => r.status === 'discrepancy').length;
      const pending = records.filter(r => r.status === 'pending').length;
      const totalAmount = records.reduce((sum, r) => sum + r.total_amount, 0);

      // Calculate accuracy (% of matched imports)
      const averageAccuracy = totalReconciliations > 0 
        ? Math.round((matched / totalReconciliations) * 100) 
        : 100;

      setStats({
        totalReconciliations,
        matched,
        discrepancies,
        pending,
        totalAmount,
        averageAccuracy,
      });
    } catch (error) {
      console.error('Error loading reconciliations:', error);
      Alert.alert('Error', 'Failed to load reconciliation data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadReconciliations();
  };

  const getImportTypeLabel = (type: string) => {
    switch (type) {
      case 'uber_income': return 'Uber Income';
      case 'salik_tolls': return 'Salik Tolls';
      case 'traffic_fines': return 'Traffic Fines';
      default: return type;
    }
  };

  const getImportTypeIcon = (type: string) => {
    switch (type) {
      case 'uber_income': return 'car';
      case 'salik_tolls': return 'navigate';
      case 'traffic_fines': return 'warning';
      default: return 'document';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched': return theme.colors.success;
      case 'discrepancy': return theme.colors.warning;
      case 'pending': return theme.colors.textSecondary;
      default: return theme.colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'matched': return 'checkmark-circle';
      case 'discrepancy': return 'alert-circle';
      case 'pending': return 'time';
      default: return 'help-circle';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(2)} AED`;
  };

  const renderStatsCards = () => (
    <View style={styles.statsContainer}>
      <Card style={styles.statCard}>
        <View style={styles.statContent}>
          <Ionicons name="documents" size={24} color={theme.colors.primary} />
          <Text style={styles.statValue}>{stats.totalReconciliations}</Text>
          <Text style={styles.statLabel}>Total Imports</Text>
        </View>
      </Card>

      <Card style={styles.statCard}>
        <View style={styles.statContent}>
          <Ionicons name="checkmark-circle" size={24} color={theme.colors.success} />
          <Text style={[styles.statValue, { color: theme.colors.success }]}>{stats.matched}</Text>
          <Text style={styles.statLabel}>Matched</Text>
        </View>
      </Card>

      <Card style={styles.statCard}>
        <View style={styles.statContent}>
          <Ionicons name="alert-circle" size={24} color={theme.colors.warning} />
          <Text style={[styles.statValue, { color: theme.colors.warning }]}>{stats.discrepancies}</Text>
          <Text style={styles.statLabel}>Discrepancies</Text>
        </View>
      </Card>

      <Card style={styles.statCard}>
        <View style={styles.statContent}>
          <Ionicons name="trophy" size={24} color={theme.colors.primary} />
          <Text style={styles.statValue}>{stats.averageAccuracy}%</Text>
          <Text style={styles.statLabel}>Accuracy</Text>
        </View>
      </Card>
    </View>
  );

  const renderFilterTabs = () => (
    <View style={styles.filterSection}>
      <Text style={styles.filterTitle}>Import Type:</Text>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.filterScroll}
      >
        {(['all', 'uber_income', 'salik_tolls', 'traffic_fines'] as ImportTypeFilter[]).map((type) => (
          <TouchableOpacity
            key={type}
            style={[
              styles.filterTab,
              importTypeFilter === type && styles.filterTabActive
            ]}
            onPress={() => setImportTypeFilter(type)}
          >
            <Text style={[
              styles.filterTabText,
              importTypeFilter === type && styles.filterTabTextActive
            ]}>
              {type === 'all' ? 'All Types' : getImportTypeLabel(type)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <Text style={[styles.filterTitle, { marginTop: 12 }]}>Status:</Text>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.filterScroll}
      >
        {(['all', 'matched', 'discrepancy', 'pending'] as StatusFilter[]).map((status) => (
          <TouchableOpacity
            key={status}
            style={[
              styles.filterTab,
              statusFilter === status && styles.filterTabActive
            ]}
            onPress={() => setStatusFilter(status)}
          >
            <Text style={[
              styles.filterTabText,
              statusFilter === status && styles.filterTabTextActive
            ]}>
              {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderReconciliationItem = (item: ReconciliationRecord) => (
    <Card key={item.id} style={styles.reconciliationCard}>
      <View style={styles.cardHeader}>
        <View style={styles.typeContainer}>
          <Ionicons 
            name={getImportTypeIcon(item.import_type) as any} 
            size={20} 
            color={theme.colors.primary} 
          />
          <Text style={styles.importType}>{getImportTypeLabel(item.import_type)}</Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) + '20' }]}>
          <Ionicons 
            name={getStatusIcon(item.status) as any} 
            size={16} 
            color={getStatusColor(item.status)} 
          />
          <Text style={[styles.statusText, { color: getStatusColor(item.status) }]}>
            {item.status.toUpperCase()}
          </Text>
        </View>
      </View>

      <View style={styles.cardBody}>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Date:</Text>
          <Text style={styles.infoValue}>{formatDate(item.import_date)}</Text>
        </View>

        {item.file_name && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>File:</Text>
            <Text style={styles.infoValue} numberOfLines={1}>{item.file_name}</Text>
          </View>
        )}

        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Records:</Text>
          <Text style={styles.infoValue}>{item.record_count}</Text>
        </View>

        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Amount:</Text>
          <Text style={styles.amountValue}>{formatCurrency(item.total_amount)}</Text>
        </View>

        {item.calculated_total !== null && item.calculated_total !== item.total_amount && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Calculated:</Text>
            <Text style={styles.amountValue}>{formatCurrency(item.calculated_total)}</Text>
          </View>
        )}

        {item.difference !== null && item.difference !== 0 && (
          <View style={styles.differenceContainer}>
            <Ionicons name="warning" size={16} color={theme.colors.warning} />
            <Text style={styles.differenceText}>
              Difference: {formatCurrency(Math.abs(item.difference))}
            </Text>
          </View>
        )}

        {item.notes && (
          <View style={styles.notesContainer}>
            <Text style={styles.notesLabel}>Notes:</Text>
            <Text style={styles.notesText}>{item.notes}</Text>
          </View>
        )}

        <View style={styles.batchIdContainer}>
          <Text style={styles.batchIdLabel}>Batch ID:</Text>
          <Text style={styles.batchIdValue}>{item.batch_id}</Text>
        </View>
      </View>
    </Card>
  );

  if (loading && !refreshing) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.loadingText}>Loading reconciliation data...</Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      <View style={styles.header}>
        <Text style={styles.title}>Import Reconciliation</Text>
        <Text style={styles.subtitle}>Verify import accuracy and detect discrepancies</Text>
      </View>

      {renderStatsCards()}
      {renderFilterTabs()}

      <View style={styles.listContainer}>
        <Text style={styles.sectionTitle}>
          Reconciliation Records ({reconciliations.length})
        </Text>

        {reconciliations.length === 0 ? (
          <Card style={styles.emptyCard}>
            <Ionicons name="document-text-outline" size={48} color={theme.colors.textSecondary} />
            <Text style={styles.emptyText}>No reconciliation records found</Text>
            <Text style={styles.emptySubtext}>
              Import data to see reconciliation status
            </Text>
          </Card>
        ) : (
          reconciliations.map(renderReconciliationItem)
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  header: {
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  loadingText: {
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  statsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 12,
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 150 : '45%',
    padding: 16,
  },
  statContent: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginTop: 8,
  },
  statLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  filterSection: {
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  filterTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  filterScroll: {
    marginBottom: 8,
  },
  filterTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: theme.colors.background,
    marginRight: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  filterTabActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  filterTabText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    fontWeight: '500',
  },
  filterTabTextActive: {
    color: '#FFFFFF',
  },
  listContainer: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  reconciliationCard: {
    marginBottom: 12,
    padding: 16,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  typeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  importType: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  cardBody: {
    gap: 8,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    fontWeight: '500',
    flex: 2,
    textAlign: 'right',
  },
  amountValue: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.primary,
    flex: 2,
    textAlign: 'right',
  },
  differenceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: theme.colors.warning + '10',
    padding: 8,
    borderRadius: 8,
    marginTop: 4,
  },
  differenceText: {
    fontSize: 14,
    color: theme.colors.warning,
    fontWeight: '600',
  },
  notesContainer: {
    backgroundColor: theme.colors.background,
    padding: 8,
    borderRadius: 8,
    marginTop: 4,
  },
  notesLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  notesText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    fontStyle: 'italic',
  },
  batchIdContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  batchIdLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  batchIdValue: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  emptyCard: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
});
